import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Sidebar from "../components/sidebar";
import Footer from "../components/footer";
import "../sass/global/mainLayout.scss";
import { clearAuthSession } from "../auth/authStorage";

/*
  Configuração operacional:
  - IDLE_TIMEOUT_MINUTES: tempo total sem atividade até efetuar logout.
  - WARNING_BEFORE_LOGOUT_MINUTES: quanto tempo antes do logout o aviso aparece.

  Exemplo atual:
  - 30 minutos de inatividade.
  - aviso nos últimos 5 minutos.
*/
const IDLE_TIMEOUT_MINUTES = 30;
const WARNING_BEFORE_LOGOUT_MINUTES = 5;

const IDLE_TIMEOUT_MS = IDLE_TIMEOUT_MINUTES * 60 * 1000;
const WARNING_BEFORE_LOGOUT_MS = WARNING_BEFORE_LOGOUT_MINUTES * 60 * 1000;
const WARNING_AT_MS = Math.max(0, IDLE_TIMEOUT_MS - WARNING_BEFORE_LOGOUT_MS);

const ACTIVITY_EVENTS = [
  "mousemove",
  "mousedown",
  "pointermove",
  "pointerdown",
  "keydown",
  "scroll",
  "wheel",
  "touchstart",
  "click",
  "input",
];

const formatRemainingTime = (seconds) => {
  const safeSeconds = Math.max(0, Number(seconds) || 0);
  const minutes = Math.floor(safeSeconds / 60);
  const restSeconds = safeSeconds % 60;

  if (minutes <= 0) {
    return `${restSeconds}s`;
  }

  return `${minutes}min ${String(restSeconds).padStart(2, "0")}s`;
};

const MainLayout = ({ children }) => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [showIdleWarning, setShowIdleWarning] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(
    Math.ceil(WARNING_BEFORE_LOGOUT_MS / 1000),
  );

  const warningTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);
  const countdownTimerRef = useRef(null);
  const deadlineRef = useRef(Date.now() + IDLE_TIMEOUT_MS);
  const lastResetRef = useRef(0);
  const isLoggingOutRef = useRef(false);

  const handleSidebarToggle = useCallback((isOpen) => {
    setIsSidebarCollapsed(!isOpen);
  }, []);

  const clearIdleTimers = useCallback(() => {
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }

    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }

    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
  }, []);

  const executeAutoLogout = useCallback(
    (motivo = "INATIVIDADE") => {
      if (isLoggingOutRef.current) return;

      isLoggingOutRef.current = true;
      clearIdleTimers();

      console.warn("[IDLE] Logout local por inatividade.", {
        motivo,
        agora: new Date().toISOString(),
        deadline: new Date(deadlineRef.current).toISOString(),
      });

      /*
       * IMPORTANTE:
       * O logout automático por inatividade é SOMENTE LOCAL.
       *
       * Não chama /ssw/token/logout e, portanto, não revoga
       * o RefreshToken no banco.
       *
       * Isso evita que um falso positivo do controle de
       * inatividade destrua a sessão de refresh do usuário.
       *
       * O logout manual do sistema deve continuar usando
       * logoutSession(), que revoga o RefreshToken no backend.
       */
      localStorage.removeItem("idChamadoSelecionado");
      clearAuthSession();

      window.location.replace("/");
    },
    [clearIdleTimers],
  );

  const startWarningCountdown = useCallback(() => {
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }

    const updateRemaining = () => {
      const secondsLeft = Math.max(
        0,
        Math.ceil((deadlineRef.current - Date.now()) / 1000),
      );

      setRemainingSeconds(secondsLeft);

      if (secondsLeft <= 0) {
        /*
         * Faz uma última validação do deadline antes do logout.
         * Evita logout causado apenas por atraso de timer do navegador.
         */
        if (Date.now() >= deadlineRef.current) {
          executeAutoLogout("CONTAGEM_INATIVIDADE_ESGOTADA");
        }
      }
    };

    setShowIdleWarning(true);
    updateRemaining();

    countdownTimerRef.current = setInterval(updateRemaining, 1000);
  }, [executeAutoLogout]);

  const resetIdleControl = useCallback(
    (force = false) => {
      if (isLoggingOutRef.current) return;

      const now = Date.now();

      /*
        Evita recriar timers dezenas de vezes por segundo em mousemove/scroll.
        Quando force=true, usado por botão/retorno de aba, ignora throttle.
      */
      if (!force && now - lastResetRef.current < 1000) {
        return;
      }

      lastResetRef.current = now;
      deadlineRef.current = now + IDLE_TIMEOUT_MS;

      if (force) {
        console.debug("[IDLE] Controle de inatividade reiniciado.", {
          agora: new Date(now).toISOString(),
          deadline: new Date(deadlineRef.current).toISOString(),
        });
      }

      clearIdleTimers();

      setShowIdleWarning(false);
      setRemainingSeconds(Math.ceil(WARNING_BEFORE_LOGOUT_MS / 1000));

      warningTimerRef.current = setTimeout(() => {
        startWarningCountdown();
      }, WARNING_AT_MS);

      logoutTimerRef.current = setTimeout(() => {
        /*
         * O timer pode disparar atrasado quando a aba fica suspensa.
         * Confere novamente o deadline real antes de encerrar.
         */
        if (Date.now() >= deadlineRef.current) {
          executeAutoLogout("TIMER_INATIVIDADE");
        }
      }, IDLE_TIMEOUT_MS);
    },
    [clearIdleTimers, executeAutoLogout, startWarningCountdown],
  );

  const handleContinueConnected = useCallback(() => {
    resetIdleControl(true);
  }, [resetIdleControl]);

  useEffect(() => {
    const onUserActivity = () => {
      resetIdleControl(false);
    };

    const onVisibilityChange = () => {
      if (document.hidden) {
        console.debug("[IDLE] Aba ficou oculta.", {
          agora: new Date().toISOString(),
          deadline: new Date(deadlineRef.current).toISOString(),
        });
        return;
      }

      const now = Date.now();
      const msLeft = deadlineRef.current - now;

      console.debug("[IDLE] Aba voltou a ficar visível.", {
        agora: new Date(now).toISOString(),
        deadline: new Date(deadlineRef.current).toISOString(),
        msLeft,
      });

      /*
       * Ao voltar para a aba, não renovamos automaticamente a
       * atividade antes de validar o tempo decorrido.
       *
       * Se o prazo realmente venceu, faz apenas logout LOCAL,
       * preservando o RefreshToken no banco.
       */
      if (msLeft <= 0) {
        executeAutoLogout("RETORNO_ABA_APOS_TIMEOUT");
        return;
      }

      if (msLeft <= WARNING_BEFORE_LOGOUT_MS) {
        startWarningCountdown();
        return;
      }

      /*
       * Se ainda está dentro do período válido, considera o retorno
       * para a aba como atividade e reinicia o controle.
       */
      resetIdleControl(true);
    };

    resetIdleControl(true);

    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, onUserActivity, {
        passive: true,
        capture: true,
      });
    });

    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      clearIdleTimers();

      ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, onUserActivity, {
          capture: true,
        });
      });

      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [
    clearIdleTimers,
    executeAutoLogout,
    resetIdleControl,
    startWarningCountdown,
  ]);

  const idleWarningMessage = useMemo(() => {
    const minutes = Math.ceil(remainingSeconds / 60);

    if (minutes <= 1) {
      return "Sua sessão será encerrada em menos de 1 minuto por inatividade.";
    }

    return `Sua sessão será encerrada em aproximadamente ${minutes} minutos por inatividade.`;
  }, [remainingSeconds]);

  return (
    <div className="main-layout-wrapper">
      <div className="main-layout">
        <Sidebar onToggle={handleSidebarToggle} />

        <div
          className={`main-content ${
            isSidebarCollapsed ? "collapsed" : "expanded"
          }`}
        >
          <div className="main-scroll-content">{children}</div>
          <Footer />
        </div>
      </div>

      {showIdleWarning && (
        <div
          className="idle-warning-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="idle-warning-title"
        >
          <div className="idle-warning-modal">
            <div className="idle-warning-icon">!</div>

            <div className="idle-warning-content">
              <h2 id="idle-warning-title">Sessão prestes a expirar</h2>
              <p>{idleWarningMessage}</p>

              <div className="idle-warning-timer">
                Tempo restante: <strong>{formatRemainingTime(remainingSeconds)}</strong>
              </div>

              <div className="idle-warning-actions">
                <button
                  type="button"
                  className="btn-idle-primary"
                  onClick={handleContinueConnected}
                >
                  Continuar conectado
                </button>

                <button
                  type="button"
                  className="btn-idle-secondary"
                  onClick={() => executeAutoLogout("USUARIO_CLICOU_SAIR_NO_AVISO")}
                >
                  Sair agora
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MainLayout;
